import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Key, Mail, Map, CreditCard, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function AdminSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/settings`);
      setSettings(response.data.settings);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch settings');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings`, settings);
      toast.success('Settings saved! Please restart backend for changes to take effect.');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (service, key, value) => {
    setSettings(prev => ({
      ...prev,
      [service]: {
        ...prev[service],
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/admin')} 
              variant="outline" 
              className="bg-white text-purple-600 hover:bg-gray-100"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-3xl font-bold">API Settings</h1>
              <p className="text-purple-100 mt-1">Manage third-party integrations</p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="twilio" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="twilio">
              <MessageSquare size={16} className="mr-2" />
              Twilio
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail size={16} className="mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="maps">
              <Map size={16} className="mr-2" />
              Google Maps
            </TabsTrigger>
            <TabsTrigger value="payment">
              <CreditCard size={16} className="mr-2" />
              Payment
            </TabsTrigger>
        <TabsTrigger value="notifications">
          Notifications
        </TabsTrigger>
          </TabsList>

          {/* Twilio Settings */}
          <TabsContent value="twilio">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare size={20} />
                  Twilio SMS & WhatsApp Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Status:</strong> <Badge className="ml-2">Connected</Badge>
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    Get your credentials from: <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a>
                  </p>
                </div>

                <div>
                  <Label htmlFor="twilio-sid">Account SID</Label>
                  <Input
                    id="twilio-sid"
                    value={settings?.twilio?.account_sid || ''}
                    onChange={(e) => updateSetting('twilio', 'account_sid', e.target.value)}
                    placeholder="AC..."
                  />
                </div>

                <div>
                  <Label htmlFor="twilio-token">Auth Token</Label>
                  <Input
                    id="twilio-token"
                    type="password"
                    value={settings?.twilio?.auth_token || ''}
                    onChange={(e) => updateSetting('twilio', 'auth_token', e.target.value)}
                    placeholder="Enter auth token"
                  />
                </div>

                <div>
                  <Label htmlFor="twilio-phone">Phone Number (with country code)</Label>
                  <Input
                    id="twilio-phone"
                    value={settings?.twilio?.phone_number || ''}
                    onChange={(e) => updateSetting('twilio', 'phone_number', e.target.value)}
                    placeholder="+447426731746"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Settings */}
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail size={20} />
                  Email SMTP Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-green-800">
                    <strong>Status:</strong> <Badge className="ml-2 bg-green-600">Active</Badge>
                  </p>
                  <p className="text-sm text-green-600 mt-2">
                    Current: {settings?.email?.from_email}
                  </p>
                </div>

                <div>
                  <Label htmlFor="smtp-host">SMTP Host</Label>
                  <Input
                    id="smtp-host"
                    value={settings?.email?.smtp_host || ''}
                    onChange={(e) => updateSetting('email', 'smtp_host', e.target.value)}
                    placeholder="smtp.livemail.co.uk"
                  />
                </div>

                <div>
                  <Label htmlFor="smtp-port">SMTP Port</Label>
                  <Input
                    id="smtp-port"
                    value={settings?.email?.smtp_port || ''}
                    onChange={(e) => updateSetting('email', 'smtp_port', e.target.value)}
                    placeholder="587"
                  />
                </div>

                <div>
                  <Label htmlFor="smtp-user">SMTP User (Email)</Label>
                  <Input
                    id="smtp-user"
                    value={settings?.email?.smtp_user || ''}
                    onChange={(e) => updateSetting('email', 'smtp_user', e.target.value)}
                    placeholder="info@romancars.uk"
                  />
                </div>

                <div>
                  <Label htmlFor="smtp-password">SMTP Password</Label>
                  <Input
                    id="smtp-password"
                    type="password"
                    value={settings?.email?.smtp_password || ''}
                    onChange={(e) => updateSetting('email', 'smtp_password', e.target.value)}
                    placeholder="Enter password"
                  />
                </div>

                <div>
                  <Label htmlFor="from-email">From Email</Label>
                  <Input
                    id="from-email"
                    value={settings?.email?.from_email || ''}
                    onChange={(e) => updateSetting('email', 'from_email', e.target.value)}
                    placeholder="info@romancars.uk"
                  />
                </div>

                <div>
                  <Label htmlFor="from-name">From Name</Label>
                  <Input
                    id="from-name"
                    value={settings?.email?.from_name || ''}
                    onChange={(e) => updateSetting('email', 'from_name', e.target.value)}
                    placeholder="Roman Cars Booking"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Google Maps Settings */}
          <TabsContent value="maps">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map size={20} />
                  Google Maps API Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Ensure Maps JavaScript API is enabled in Google Cloud Console
                  </p>
                  <p className="text-sm text-yellow-600 mt-2">
                    Get your API key from: <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a>
                  </p>
                </div>

                <div>
                  <Label htmlFor="maps-key">Google Maps API Key</Label>
                  <Input
                    id="maps-key"
                    type="password"
                    value={settings?.google_maps?.api_key || ''}
                    onChange={(e) => updateSetting('google_maps', 'api_key', e.target.value)}
                    placeholder="AIza..."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Settings */}
          <TabsContent value="payment">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard size={20} />
                  Stripe Payment Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>Status:</strong> {<Badge className="ml-2 bg-green-600">Live Mode</Badge>}
                  </p>
                  <p className="text-sm text-purple-600 mt-2">
                    Get your keys from: <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard</a>
                  </p>
                </div>

                <div>
                  <Label htmlFor="stripe-key">Stripe API Key (Secret Key)</Label>
                  <Input
                    id="stripe-key"
                    type="password"
                    value={settings?.stripe?.api_key || ''}
                    onChange={(e) => updateSetting('stripe', 'api_key', e.target.value)}
                    placeholder="sk_test_..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use test key (sk_test_...) for testing, live key (sk_live_...) for production
                  </p>
                </div>
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Booking Notification Emails</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">Add email addresses to receive booking notifications.</p>
              
              <div className="space-y-2">
                {(settings?.notification_emails || []).map((email, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input value={email} readOnly className="flex-1" />
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                      onClick={async () => {
                        const emails = [...(settings?.notification_emails || [])];
                        emails.splice(index, 1);
                        await axios.post(API + '/api/admin/settings', { notification_emails: emails });
                        fetchSettings();
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center gap-2">
                <Input
                  id="new-notify-email"
                  type="email"
                  placeholder="Enter email address"
                />
                <Button
                  onClick={async () => {
                    const input = document.getElementById('new-notify-email');
                    if (input.value && input.value.includes('@')) {
                      const emails = [...(settings?.notification_emails || []), input.value];
                      await axios.post(API + '/api/admin/settings', { notification_emails: emails });
                      input.value = '';
                      fetchSettings();
                    }
                  }}
                >
                  Add Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Warning */}
        <div className="mt-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-sm text-red-800">
            <strong>⚠️ Important:</strong> After saving changes, you must restart the backend server for the new settings to take effect.
          </p>
          <p className="text-sm text-red-600 mt-2">
            Command: <code className="bg-red-100 px-2 py-1 rounded">sudo supervisorctl restart backend</code>
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminSettingsPage;
